import React, { useEffect, useState } from "react";
import { DollarSign, ShoppingBag, TrendingUp } from "lucide-react";
import { api } from "@/api/client";
import { useManageBusiness } from "@/pages/manage/ManageShell";
import { money } from "@/lib/pricing";

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

export default function Reports() {
  const { businessId } = useManageBusiness();
  const [days, setDays] = useState(7);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getSalesReport({ business_id: businessId, days }).then(setReport).catch(() => {}).finally(() => setLoading(false));
  }, [businessId, days]);

  const cards = report
    ? [
        { label: "Revenue", value: money(report.total_revenue), icon: DollarSign },
        { label: "Orders", value: report.total_orders, icon: ShoppingBag },
        { label: "Avg order", value: money(report.average_order_value), icon: TrendingUp },
      ]
    : [];

  const maxRev = report ? Math.max(...report.daily.map((d) => d.revenue), 1) : 1;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Reports</h1>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button key={r.days} onClick={() => setDays(r.days)} data-testid={`range-${r.days}`} className="px-3.5 py-1.5 rounded-full text-sm font-semibold transition" style={days === r.days ? { background: "var(--brand)", color: "var(--brand-fg)" } : { background: "var(--surface)", color: "var(--muted)", border: "1px solid var(--border)" }}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading || !report ? (
        <div className="mt-6 h-64 rounded-2xl animate-pulse" style={{ background: "var(--surface-2)" }} />
      ) : (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                  <div className="flex items-center justify-between"><span className="text-sm font-semibold" style={{ color: "var(--muted)" }}>{c.label}</span><Icon size={18} style={{ color: "var(--brand)" }} /></div>
                  <div className="font-display text-3xl mt-2" style={{ color: "var(--ink)" }}>{c.value}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
            <h2 className="font-display text-xl mb-4" style={{ color: "var(--ink)" }}>Daily revenue</h2>
            <div className="flex items-end gap-1.5 h-48 overflow-x-auto">
              {report.daily.map((d) => (
                <div key={d.date} className="flex-1 min-w-[14px] flex flex-col items-center gap-1.5 group">
                  <div className="text-[10px] opacity-0 group-hover:opacity-100 transition" style={{ color: "var(--ink)" }}>{money(d.revenue)}</div>
                  <div className="w-full rounded-t-md transition-all" style={{ height: `${Math.max((d.revenue / maxRev) * 100, 3)}%`, background: "var(--brand)", opacity: d.revenue ? 1 : 0.25 }} />
                  <span className="text-[9px]" style={{ color: "var(--muted)" }}>{d.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
            <h2 className="font-display text-xl mb-4" style={{ color: "var(--ink)" }}>Top products</h2>
            {report.top_products.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--muted)" }}>No sales in this period.</p>
            ) : (
              <div className="space-y-3">
                {report.top_products.map((p, i) => (
                  <div key={p.product_id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: "var(--accent-soft)", color: "var(--brand)" }}>{i + 1}</span>
                      <div>
                        <div className="font-semibold" style={{ color: "var(--ink)" }}>{p.product_name}</div>
                        <div className="text-xs" style={{ color: "var(--muted)" }}>{p.quantity} sold · {p.orders} orders</div>
                      </div>
                    </div>
                    <div className="font-display text-lg" style={{ color: "var(--brand)" }}>{money(p.revenue)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
